// src/context/LocalContext.jsx
import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { doc, getDoc } from "firebase/firestore";
import db from "../firebase/firebaseConfig";

const LocalContext = createContext();

/* ----------------------------- utils ----------------------------- */
const getWin = () => (typeof window !== "undefined" ? window : null);

const isLikelyMobile = () => {
  const w = getWin();
  if (!w) return false;
  const ua = w.navigator?.userAgent || "";
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua);
};

/* ============================== Provider ============================== */
export const LocalProvider = ({ children }) => {
  /* ---- TFN (phone numbers) ---- */
  const [currentTFN, setCurrentTFN] = useState({ intlFormat: "", localFormat: "" });

  /* ---- Web/site info ---- */
  const [webinfo, setwebinfo] = useState({
    name: "Protrexa",
    phone: "",
    phonecall: "",
    logo:
      "https://res.cloudinary.com/duv3inafo/image/upload/v1758302608/d066c4dc-02a3-4fd7-baf8-3e961c7f4854-removebg-preview_nsb2qs.png",
    email: "contact@protrexa.com",
    address: "19, Ashoka Rd, Janpath, Connaught Place, New Delhi, Delhi 110001, India",
    addressCity: "New Delhi",
    telegramHandle: "darioharmon", // without '@'
  });

  /* ---- Fetch TFN from Firestore ---- */
  useEffect(() => {
    const fetchTFN = async () => {
      try {
        const docRef = doc(db, "siteNumbers", "weboku.com");
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setCurrentTFN({
            intlFormat: data.numberIntl || "",
            localFormat: data.numberLocal || "",
          });
        } else {
          setCurrentTFN({ intlFormat: "", localFormat: "" });
          console.log("siteNumbers/weboku.com not found");
        }
      } catch (err) {
        console.error("Error fetching TFN:", err);
      }
    };
    fetchTFN();
  }, []);

  /* ---- Map TFN → webinfo ---- */
  useEffect(() => {
    setwebinfo((prev) => ({
      ...prev,
      phone: currentTFN.localFormat || prev.phone,
      phonecall: currentTFN.intlFormat || prev.phonecall,
    }));
  }, [currentTFN]);

  /* ---------------------- Telegram helpers ---------------------- */
  const getTelegramUrl = ({ handle, preferApp } = {}) => {
    const h = (handle || webinfo.telegramHandle || "").replace(/^@/, "").trim();
    if (!h) return "";
    const useApp = typeof preferApp === "boolean" ? preferApp : isLikelyMobile();
    return useApp ? `tg://resolve?domain=${encodeURIComponent(h)}` : `https://t.me/${encodeURIComponent(h)}`;
  };

  const openTelegram = ({ handle, preferApp } = {}) => {
    const w = getWin();
    const url = getTelegramUrl({ handle, preferApp });
    if (!w || !url) return false;

    try {
      const popup = w.open(url, "_blank", "noopener,noreferrer");
      if (popup) return true;
      w.location.href = url;
      return true;
    } catch (e) {
      // fallback https if tg:// blocked
      if (url.startsWith("tg://")) {
        const httpsUrl = getTelegramUrl({ handle, preferApp: false });
        try {
          const popup2 = w.open(httpsUrl, "_blank", "noopener,noreferrer");
          if (popup2) return true;
          w.location.href = httpsUrl;
          return true;
        } catch {
          w.location.href = httpsUrl;
          return true;
        }
      }
      return false;
    }
  };

  const getTelegramOnClick = ({ handle, preferApp } = {}) => (e) => {
    e?.preventDefault?.();
    openTelegram({ handle, preferApp });
  };

  /* -------------------- Companies dataset + helpers -------------------- */
  // Keep service lists out of here; only “company/brand by region” lives here.
  const companies = useMemo(
    () => ({
      // Printer (top brands/providers)
      "printer-support": {
        USA: ["HP", "Brother", "Canon", "Epson", "Xerox", "Lexmark"],
        UK: ["HP", "Brother", "Canon", "Epson", "Kyocera"],
        AUS: ["HP", "Brother", "Epson", "Canon", "FUJIFILM Business Innovation"],
      },

      // Antivirus / endpoint suites
      antivirus: {
        USA: ["Norton", "McAfee", "Bitdefender", "Kaspersky", "Trend Micro", "ESET", "Sophos", "Avast", "AVG", "Webroot"],
        UK: ["Sophos", "ESET", "Bitdefender", "Kaspersky", "Norton", "McAfee", "Avast", "AVG", "Trend Micro", "Malwarebytes"],
        AUS: ["Trend Micro", "Norton", "Bitdefender", "ESET", "Kaspersky", "Sophos", "Avast", "Webroot"],
      },

      // Optional tech ecosystems by region (showcase only)
      microsoft: {
        USA: ["Microsoft 365", "Azure", "Defender", "Entra ID"],
        UK: ["Microsoft 365", "Azure", "Defender", "Entra ID"],
        AUS: ["Microsoft 365", "Azure", "Defender", "Entra ID"],
      },
      "accounting-bookkeeping": {
        USA: ["QuickBooks", "Xero", "Zoho Books", "Sage"],
        UK: ["Xero", "Sage", "QuickBooks", "FreeAgent"],
        AUS: ["Xero", "MYOB", "QuickBooks", "Zoho Books"],
      },
      "web-development": {
        USA: ["React", "WordPress", "Next.js", "Shopify"],
        UK: ["React", "WordPress", "Next.js", "Shopify"],
        AUS: ["React", "WordPress", "Next.js", "Shopify"],
      },
      "crm-development": {
        USA: ["HubSpot", "Salesforce", "Zoho CRM", "Pipedrive"],
        UK: ["HubSpot", "Salesforce", "Zoho CRM", "Pipedrive"],
        AUS: ["HubSpot", "Salesforce", "Zoho CRM", "Pipedrive"],
      },
    }),
    []
  );

  const getCompanies = (serviceSlug) => companies?.[serviceSlug] || null;
  const getCompaniesByRegion = (serviceSlug, region) =>
    (companies?.[serviceSlug] && companies[serviceSlug][region]) || [];
  const listCompanyRegions = (serviceSlug) => Object.keys(companies?.[serviceSlug] || {});

  /* ------------------------------ value ------------------------------ */
  const value = useMemo(
    () => ({
      webinfo,
      setwebinfo,

      // Telegram
      getTelegramUrl,
      openTelegram,
      getTelegramOnClick,

      // Companies
      companies,
      getCompanies,
      getCompaniesByRegion,
      listCompanyRegions,
    }),
    [webinfo, companies]
  );

  return <LocalContext.Provider value={value}>{children}</LocalContext.Provider>;
};

/* ------------------------------ hook ------------------------------ */
export const useLocalContext = () => useContext(LocalContext);
